/**
 * The display-timezone seam.
 *
 * Everything user-facing that renders a moment in time should go through here,
 * with the zone from `useDisplayTz()`. The point is not formatting; it is that
 * the zone becomes an explicit argument instead of an ambient property of
 * whichever machine happens to be rendering.
 *
 * Two structurally separate entry points, so the commonest date bug cannot be
 * written by accident in either direction:
 *
 *   formatDateOnly(plainDate)  a calendar date ("2026-09-12"). Takes NO zone,
 *                              because a birthday or a start date does not have
 *                              one. Never calls `new Date(string)`, which would
 *                              parse it as UTC midnight and render as the day
 *                              before for anyone west of Greenwich.
 *
 *   formatInstant(iso, tz)     a real moment (a timestamptz). Requires a zone,
 *                              and refuses a date-only value rather than
 *                              guessing which midnight was meant.
 *
 * The zone comes from resolveDisplayTz. Never from the browser at render time:
 * a value that follows the device gives the same user two different answers on
 * two machines, and gives the server no way to reproduce either.
 */

import { logger } from "../../services/logger.service";

/**
 * Last-resort display zone. Must stay identical to FLOOR_DISPLAY_TZ in
 * server/utils/displayTimezone.ts, or a client-rendered header and the
 * server-cut buckets underneath it will disagree about which day is which.
 *
 * UTC, not a US zone: SkyHire serves Indian and US tenants from one deployment,
 * so any regional default is confidently wrong for half of them.
 */
export const FLOOR_DISPLAY_TZ = "UTC";

/**
 * The zone to render in. Total: never throws, never returns the device zone.
 *
 *   the user's stored display zone  ->  FLOOR_DISPLAY_TZ
 *
 * The instance timezone is deliberately absent. It looks like the natural
 * middle step and it is not available to take it: getInstance() is a client
 * stub returning "US/Pacific" for every instance, so putting it in the chain
 * would floor every unset user to Pacific while looking tenant-aware. If a real
 * per-instance zone lands later, this is the one function to add it to, and the
 * server mirror in server/utils/displayTimezone.ts has to change with it.
 */
/**
 * The IANA name shape, repeated here rather than imported because this module
 * deliberately has no imports. Identical to IANA_ZONE_SHAPE in
 * src/common/constants/displayTimezones.ts and server/utils/displayTimezone.ts;
 * a test compares all three. See the client copy for why Intl alone is not
 * enough.
 */
const IANA_ZONE_SHAPE = /^(?:UTC|[A-Z][A-Za-z_+-]*(?:\/[A-Z][A-Za-z0-9_+-]*)+)$/;

export function resolveDisplayTz(userTz: string | null | undefined): string {
  if (!userTz) return FLOOR_DISPLAY_TZ;
  // A raw offset or an abbreviation passes Intl but is read differently by
  // Postgres, so it would render right here and cut "Today" wrong on the server.
  if (!IANA_ZONE_SHAPE.test(userTz)) return FLOOR_DISPLAY_TZ;
  // A stored zone can go stale: tzdata retires names, and rows arrive from
  // restores and manual fixes that never passed the API's validation. An
  // unresolvable zone throws a RangeError inside Intl, which unmounts the
  // subtree that was only trying to draw a timestamp. Probe, then floor.
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: userTz });
    return userTz;
  } catch {
    return FLOOR_DISPLAY_TZ;
  }
}

/**
 * Intl.DateTimeFormat cache, keyed by locale plus the full options object.
 *
 * Constructing the formatter is the expensive half of toLocaleString, and a
 * table of candidates calls it with identical arguments once per row per
 * render. Keying on every option, not just the zone, keeps callsites with
 * different formats from colliding.
 */
const formatterCache = new Map<string, Intl.DateTimeFormat>();

function getFormatter(locale: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale} ${JSON.stringify(opts)}`;
  let formatter = formatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, opts);
    formatterCache.set(key, formatter);
  }
  return formatter;
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Render a calendar date ("2026-09-12") with no timezone involved at all.
 *
 * The parts are read off the string and handed to a UTC formatter built from
 * those same parts, so the day that goes in is the day that comes out, on every
 * machine. Returns "" for null/empty and the input unchanged if it is not a
 * plain date, matching dateFormatter's behaviour for unparseable input.
 */
export function formatDateOnly(
  plainDate: string | null | undefined,
  opts?: { locale?: string } & Intl.DateTimeFormatOptions,
): string {
  if (plainDate == null) return "";
  const s = String(plainDate).trim();
  if (!s) return "";
  const match = DATE_ONLY.exec(s);
  if (!match) return s;
  const [year, month, day] = s.split("-").map(Number);
  const { locale = "en-US", ...rest } = opts ?? {};
  return getFormatter(locale, {
    timeZone: "UTC",
    year: "numeric",
    month: "short",
    day: "numeric",
    ...rest,
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

/**
 * Render a UTC instant (an ISO timestamptz) in an explicit IANA zone.
 *
 * Returns "" for null/empty/unparseable, matching dateFormatter. A date-only
 * value is NOT silently midnight-guessed into an instant: it is routed to
 * formatDateOnly, which renders the day it actually says, and warned about, so
 * the wrong callsite is findable without white-screening a page over it.
 */
export function formatInstant(
  iso: string | Date | null | undefined,
  tz: string,
  opts?: { showTime?: boolean; locale?: string } & Intl.DateTimeFormatOptions,
): string {
  if (iso == null) return "";
  const { showTime = true, locale = "en-US", ...rest } = opts ?? {};

  let date: Date;
  if (iso instanceof Date) {
    date = iso;
  } else {
    const s = String(iso).trim();
    if (!s) return "";
    if (DATE_ONLY.test(s)) {
      logger.warn(
        "[displayTime] formatInstant received a date-only value; use formatDateOnly() at this callsite",
      );
      return formatDateOnly(s, { locale, ...rest });
    }
    date = new Date(s);
  }
  if (isNaN(date.getTime())) return "";

  return getFormatter(locale, {
    timeZone: tz,
    year: "numeric",
    month: "short",
    day: "numeric",
    ...(showTime ? { hour: "numeric", minute: "2-digit" } : {}),
    ...rest,
  }).format(date);
}

/**
 * Just the time of day of an instant in an explicit zone ("2:00 PM"), for the
 * common list layout that puts the date on one line and the time on the next.
 */
export function formatInstantTime(
  iso: string | Date | null | undefined,
  tz: string,
  locale = "en-US",
): string {
  return formatInstant(iso, tz, {
    locale,
    year: undefined,
    month: undefined,
    day: undefined,
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * A value that can be either kind, for a table column that mixes them: an
 * "Applied" timestamp beside a "Hire date" calendar date. A plain "YYYY-MM-DD"
 * prints as the day it says; anything else is an instant and prints as its day
 * in `tz`. Date only, no time. Returns "" when the value cannot be read.
 */
export function formatDateOrInstant(
  value: unknown,
  tz: string,
  opts?: { locale?: string } & Intl.DateTimeFormatOptions,
): string {
  if (value == null) return "";
  if (value instanceof Date) return formatInstant(value, tz, { ...opts, showTime: false });
  const s = String(value).trim();
  if (DATE_ONLY.test(s)) return formatDateOnly(s, opts);
  return formatInstant(s, tz, { ...opts, showTime: false });
}

/**
 * The "YYYY-MM-DD" calendar day an instant falls on IN `tz`. The client twin of
 * tzDayKey in server/utils/displayTimezone.ts; use it when a chart skeleton or a
 * grouping key has to line up with days the server already cut.
 */
export function tzDayKey(instant: Date | string, tz: string): string {
  return new Date(instant).toLocaleDateString("en-CA", { timeZone: tz });
}
